import React, { useState, useEffect, useCallback } from 'react';
import { commonStyles } from '../../../../common/components/Layout/DashboardLayout';
import classNames from 'classnames';

const SettingField = ({ label, value, onChange, readOnly = false }) => (
  <div className="mb-4">
    <label className={commonStyles.form.label}>
      {label}
    </label>
    <input
      type="number"
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      className={classNames(
        commonStyles.form.input,
        readOnly && 'bg-gray-50 dark:bg-gray-700'
      )}
    />
  </div>
);

const SettingsForm = ({ wsStatus, setWsStatus }) => {
  const [socket, setSocket] = useState(null);
  const [currentSettings, setCurrentSettings] = useState({
    upperLimit: '',
    lowerLimit: '',
    csvSaveTime: '',
    dataSendInterval: '',
    signalStrengthThreshold: '',
    deviceType: 'None'
  });

  const [newSettings, setNewSettings] = useState({
    upperLimit: '',
    lowerLimit: '',
    csvSaveTime: '',
    dataSendInterval: '',
    signalStrengthThreshold: ''
  });

  const [loading, setLoading] = useState(false);

  const connectWebSocket = useCallback(() => {
    // WebSocket URLを作成する前にtrimで余分なスペースを削除
    const wsUrl = process.env.REACT_APP_TMS000_WEBSOCKET_URL.trim();
    console.log('Connecting to WebSocket URL:', wsUrl); // デバッグ用
    const ws = new WebSocket(wsUrl);
    ws.onopen = () => {
      console.log('WebSocket Connected');
      setSocket(ws);
      setWsStatus('接続中');
      ws.send(JSON.stringify({ action: 'get_config' }));
    };
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'config_update') {
        const updatedConfig = {
          upperLimit: data.config.上限値?.toString() || '',
          lowerLimit: data.config.下限値?.toString() || '',
          csvSaveTime: data.config.CSV保存時間?.toString() || '',
          dataSendInterval: data.config.データ送信間隔?.toString() || '',
          signalStrengthThreshold: data.config.電波強度閾値?.toString() || '',
          deviceType: data.config.device_type || 'None'
        };
        setCurrentSettings(updatedConfig);
        setNewSettings(updatedConfig);
        setLoading(false);
      } else if (data.type === 'status_update') {
        setWsStatus(data.ws_status);
      }
    };
    ws.onclose = () => {
      console.log('WebSocket Disconnected');
      setSocket(null);
      setWsStatus('未接続');
    };
    return ws;
  }, [setWsStatus]);

  useEffect(() => {
    const ws = connectWebSocket();
    return () => {
      ws.close();
    };
  }, [connectWebSocket]);

  const handleNewSettingChange = (field) => (e) => {
    setNewSettings(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleFetchCurrentSettings = () => {
    setLoading(true);
    if (socket) {
      socket.send(JSON.stringify({ action: 'get_config' }));
    } else {
      alert('WebSocket接続がありません。');
      setLoading(false);
    }
  };

  const handleUpdateSettings = (e) => {
    e.preventDefault();
    setLoading(true);

    if (parseInt(newSettings.upperLimit) > 150) {
      alert('上限値は150以内で設定してください');
      setLoading(false);
      return;
    }

    if (parseInt(newSettings.lowerLimit) >= parseInt(newSettings.upperLimit)) {
      alert('下限値 < 上限値 としてください。');
      setLoading(false);
      return;
    }

    const configData = {
      "上限値": parseInt(newSettings.upperLimit) || 0,
      "下限値": parseInt(newSettings.lowerLimit) || 0,
      "CSV保存時間": parseInt(newSettings.csvSaveTime) || 0,
      "データ送信間隔": parseInt(newSettings.dataSendInterval) || 0,
      "電波強度閾値": parseInt(newSettings.signalStrengthThreshold) || 0
    };

    if (socket) {
      socket.send(JSON.stringify({ action: 'send_config', config: configData }));
    } else {
      alert('WebSocket接続がありません。');
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <h2 className={classNames(commonStyles.text.title, "mb-4 lg:mb-6 text-center")}>
        TMS#000 Config
      </h2>
      
      <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 overflow-auto">
        {/* 現在の設定 */}
        <div className={classNames(
          commonStyles.card.padding.default,
          "bg-white/50 dark:bg-gray-800/50"
        )}>
          <h3 className={classNames(commonStyles.text.subtitle, "mb-3 lg:mb-4 text-center")}>
            Now Config
          </h3>
          <div className="space-y-3 lg:space-y-4">
            <div className="space-y-4 lg:space-y-6">
              <SettingField 
                label="上限値："
                value={currentSettings.upperLimit}
                readOnly
              />
              <SettingField 
                label="下限値："
                value={currentSettings.lowerLimit}
                readOnly
              />
              <SettingField 
                label="CSV保存時間 (秒)："
                value={currentSettings.csvSaveTime}
                readOnly
              />
              <SettingField 
                label="データ送信間隔 (秒)："
                value={currentSettings.dataSendInterval}
                readOnly
              />
              <SettingField 
                label="電波強度閾値 (dBm)："
                value={currentSettings.signalStrengthThreshold}
                readOnly
              />
            </div>
            <button
              onClick={handleFetchCurrentSettings}
              disabled={loading}
              className={classNames(
                commonStyles.form.button,
                "w-full bg-green-600 hover:bg-green-700",
                loading && "opacity-50 cursor-not-allowed"
              )}
            >
              現在値を再取得
            </button>
          </div>
        </div>

        {/* 新しい設定 */}
        <div className={classNames(
          commonStyles.card.padding.default,
          "bg-white/50 dark:bg-gray-800/50"
        )}>
          <h3 className={classNames(commonStyles.text.subtitle, "mb-3 lg:mb-4 text-center")}>
            New Config
          </h3>
          <form onSubmit={handleUpdateSettings}>
            <div className="space-y-3 lg:space-y-4">
              <div className="space-y-4 lg:space-y-6">
                <SettingField 
                  label="上限値："
                  value={newSettings.upperLimit}
                  onChange={handleNewSettingChange('upperLimit')}
                />
                <SettingField 
                  label="下限値："
                  value={newSettings.lowerLimit}
                  onChange={handleNewSettingChange('lowerLimit')}
                />
                <SettingField 
                  label="CSV保存時間 (秒)："
                  value={newSettings.csvSaveTime}
                  onChange={handleNewSettingChange('csvSaveTime')}
                />
                <SettingField 
                  label="データ送信間隔 (秒)："
                  value={newSettings.dataSendInterval}
                  onChange={handleNewSettingChange('dataSendInterval')}
                />
                <SettingField 
                  label="電波強度閾値 (dBm)："
                  value={newSettings.signalStrengthThreshold}
                  onChange={handleNewSettingChange('signalStrengthThreshold')}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className={classNames(
                  commonStyles.form.button,
                  "w-full bg-green-600 hover:bg-green-700",
                  loading && "opacity-50 cursor-not-allowed"
                )}
              >
                設定を更新
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-auto pt-2 lg:pt-4">
        <span className={commonStyles.text.body}>
          最終更新: {currentSettings.deviceType}
        </span>
      </div>
    </div>
  );
};

export default SettingsForm; 